import React, { useContext, useMemo, useState, useEffect } from 'react';
import classNames from 'classnames';
import _ from 'underscore';

import { Stack } from '@fluentui/react';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { useWizardService, matchStepState, STEP_STATES, getWizardData } from '@bingads-webui-react/wizard';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { useFormService } from '@bingads-webui-react/form';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { buildLegacyI18n } from '@bingads-webui/build-legacy-i18n';
import { ValidationStringMap } from '@bingads-webui/validation-string-map';
import { Collapse, useCollapseState, ValidatedPanel, getPanelErrorProps } from '@bingads-webui-campaign/wizard-jumbo-card';
import { Campaign } from '@bingads-webui/campaign-odata-schemas';
import { ValidationGroup } from '@bingads-webui-react/validation';
import { FormErrorGroup, UnmappedFormErrors } from '@bingads-webui-campaign-react/form-error';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { AttentionBeaconGroup } from '@bingads-webui-campaign-react/attention-beacon';
import { ActuationGroup } from '@bingads-webui-campaign-react/actuator';
import {
  AudienceBudgetBidStrategy,
  AudienceBidValue,
  StepHeader,
  SectionShimmer,
  bidValueIcon,
  budgetBidStrategyIcon,
} from '@bingads-webui-campaign-react/display-plus-campaigns';
import { getBidBudgetTitleDetails } from '@bingads-webui-campaign-react/wizard-shared-components';
import { formatBidValue } from '@bingads-webui-campaign-react/summary-page';

import { getStyles } from './styles';
import { getBudgetBidSections, isAutoBidding } from '../step-utils';
import { TextTitle } from '../../shared/pannel-header';

export const BudgetBidStep = ({
  data,
  onDataChange,
  error,
}) => {
  const classes = useDefaultStyles(getStyles);
  const activity = useContext(ActivityContext);

  const { state } = useWizardService();
  const { facInfo } = getWizardData(state);
  const currentStates = state.toStrings()[1];
  const { state: { context: { saveFailed: showAllValidationErrors } } } = useFormService();

  const {
    campaign,
    adGroup,
  } = data || {};

  const i18n = useContext(I18nContext);
  const legacyI18n = useMemo(() => buildLegacyI18n(i18n, [ValidationStringMap]), [i18n]);
  const biddingSchemeType = _.get(campaign, ['BiddingScheme', 'Type'], '');

  const [sharedBudgetView, setSharedBudgetView] = useState(null);
  const serverContext = useContext(ServerContext);
  const { CeilingValue, FloorValue } = serverContext.ProductGroupBidRange || {};

  const { basicOptions } = getBudgetBidSections();
  const [optionsShown, setOptionsShown] = useState(basicOptions);

  const onValidate = (result) => {
    if (campaign) {
      let adGroupData = {};
      const biddingScheme = _.get(result, ['data', 'BiddingScheme'], {});
      const currentBiddingSchemeType = biddingScheme.Type || biddingSchemeType;

      if (isAutoBidding(currentBiddingSchemeType)) {
        // for auto bidding strategies we hide the bid panel
        const filterOutBidPanel = optionsShown.filter(option => option !== 'bid-value');
        adGroupData = {
          SearchBid: null,
        };
        setOptionsShown([...filterOutBidPanel]);
      } else {
        setOptionsShown([...basicOptions]);

        if (result.data) {
          adGroupData = {
            SearchBid: result.data.SearchBid,
          };
        }
      }

      if (!_.isUndefined(biddingScheme.BidStrategyId)) {
        campaign.BidStrategyName = biddingScheme.BidStrategyName;
      }

      onDataChange({
        data: {
          ...data,
          campaign: {
            ...campaign,
            ...result.data,
          },
          adGroup: {
            ...adGroup,
            PricingModel: result.data.BiddingScheme && result.data.BiddingScheme.Type,
            ...adGroupData,
          },
        },
        isValid: result.isValid,
      });
    }
  };

  const [
    allBudgetBidSections,
    budgetBidExpandedSections,
    setBudgetBidExpandedSections,
  ] = useCollapseState(
    optionsShown /* all sections */,
    ['bid-and-budget', 'bid-value'] /* initially expanded sections */
  );

  const budgetBidStrategyTitleDetails = useMemo(() => (
    campaign ? getBidBudgetTitleDetails(serverContext, i18n, campaign.BiddingScheme, campaign.Budget) : ''
  ), [campaign, i18n, serverContext]);

  const bidTitleDetails = useMemo(() => (
    adGroup ? formatBidValue(i18n, adGroup.SearchBid, serverContext.CurrentAccount.CurrencyCode) : ''
  ), [adGroup, i18n, serverContext]);

  useEffect(() => {
    if (facInfo && facInfo.current.isFACCouponActive && sharedBudgetView) {
      sharedBudgetView.budgetView.ko_model.updateFeatureAdoptionCoupon({
        showFACHint: true,
        couponValue: facInfo.current.facCouponValue,
      });
    }
  }, [facInfo, sharedBudgetView]);

  useEffect(() => {
    if (activity && matchStepState(state, STEP_STATES.LOADING)) {
      activity.signal(`onBudgetBidStepLoading - ${currentStates}`);
    }

    if (activity && matchStepState(state, STEP_STATES.SAVING)) {
      activity.signal(`onBudgetBidStepSaving - ${currentStates}`);
    }
  }, [activity, currentStates, state]);

  return (
    <div className={classNames(classes.budgetBidShell, 'budget-bid-shell')}>
      <div className="budget-bid-view">
        <ActuationGroup actuationProps={{ isActive: false }}>
          <AttentionBeaconGroup>
            <FormErrorGroup formErrorProps={{ errors: error || [], showAllValidationErrors }}>
              <ValidationGroup
                validationProps={{
                  schema: Campaign,
                  onValidate,
                  data: campaign || {},
                  i18n: legacyI18n,
                }}
              >
                <div className={classNames('budget-bid-step')}>
                  <div>
                    <Stack token={{ childrenGap: 14 }}>
                      <StepHeader
                        title={_TL_('How much do you want to spend?')}
                        description={_TL_('Before you can start running ads for this campaign, please set a budget.')}
                      />
                    </Stack>
                    <div className="server-errors">
                      <UnmappedFormErrors />
                    </div>
                  </div>
                  <Stack horizontal token={{ childrenGap: 14 }}>
                    <Collapse allPanelItems={allBudgetBidSections} checkItems skipItemsTypeCheck activeKeys={budgetBidExpandedSections} setActiveKeys={setBudgetBidExpandedSections}>
                      <ValidatedPanel
                        key="bid-and-budget"
                        title={
                          <TextTitle
                            titleKey={_TL_('Budget and Bid Strategy')}
                            icon={budgetBidStrategyIcon}
                            topicKey="pop_BA_CampaignCreate_Budget_DefaultBid"
                            details={budgetBidStrategyTitleDetails}
                          />}
                        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                      >
                        {
                          campaign ? (
                            <AudienceBudgetBidStrategy
                              data={data}
                              onDataChange={onDataChange}
                              campaignType={campaign.CampaignType.toLowerCase()}
                              campaignSubType={campaign.CampaignSubType}
                              setBudgetView={setSharedBudgetView}
                              activity={activity}
                            />
                          ) : (<SectionShimmer count={3} />)
                        }
                      </ValidatedPanel>
                      <ValidatedPanel
                        key="bid-value"
                        title={
                          <TextTitle
                            titleKey={_TL_('Default bid')}
                            icon={bidValueIcon}
                            topicKey="pop_BA_CampaignCreate_Budget_DefaultBid"
                            details={bidTitleDetails}
                          />}
                        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                      >
                        {
                          adGroup ? (
                            <AudienceBidValue
                              data={data}
                              onDataChange={onDataChange}
                              legacyI18n={legacyI18n}
                              ceilingValue={CeilingValue}
                              floorValue={FloorValue}
                              stopPropagation={false}
                            />
                          ) : (<SectionShimmer count={3} />)
                        }
                      </ValidatedPanel>
                    </Collapse>
                  </Stack>
                </div>
              </ValidationGroup>
            </FormErrorGroup>
          </AttentionBeaconGroup>
        </ActuationGroup>
      </div>
    </div>
  );
};
